import { Activity, Coins, Crown, Layers3, Loader2, Repeat2, ShieldCheck, TrendingUp, Trophy, Users2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fetchAdminConfig, fetchAdminPlatformStats, fetchAdminProfit } from '@/api/admin';
import ExternalPnLCard from '@/components/admin/widgets/ExternalPnLCard';
import InternalPnLCard from '@/components/admin/widgets/InternalPnLCard';
import { fmtUsdAmount as fmtUsd } from '@/lib/money';

const COLOR_GOLD = '#FFC20E';
const COLOR_PROFIT = '#10B981';

const fmtInt = (raw) => {
  const n = Number(raw || 0);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-US');
};

const secondsSince = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 1000));
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div
    className="rounded-xl p-3 md:p-4 border"
    style={{
      background: 'rgba(0,0,0,0.32)',
      borderColor: 'rgba(255, 182, 18, 0.18)',
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
      <Icon className="w-3.5 h-3.5 text-[#FFC20E]" />
      {label}
    </div>
    <div className="sc-tabular font-display font-bold text-2xl md:text-3xl mt-1.5 leading-none sc-gold-text" data-testid={`${testid}-value`}>
      {value}
    </div>
  </div>
);

const ProfitCeilingGauge = ({ current, ceiling }) => {
  const c = Number(current || 0);
  const max = Number(ceiling || 0);
  if (!Number.isFinite(c) || !Number.isFinite(max) || max <= 0) return null;
  const pct = Math.max(0, Math.min(100, (c / max) * 100));
  const reached = c >= max;
  return (
    <div className="mt-5 max-w-2xl mx-auto" data-testid="profit-ceiling-gauge">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
        <span>Progress to Ceiling</span>
        <span className="sc-tabular">
          ${fmtUsd(c)} / ${fmtUsd(max)} · {pct.toFixed(1)}%
        </span>
      </div>
      <div
        className="mt-2 h-2 rounded-full overflow-hidden border"
        style={{
          background: 'rgba(0,0,0,0.4)',
          borderColor: 'rgba(255,182,18,0.2)',
        }}
      >
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: reached ? `linear-gradient(90deg, ${COLOR_PROFIT}, #34D399)` : `linear-gradient(90deg, ${COLOR_GOLD}, #FFB612)`,
            boxShadow: reached ? `0 0 16px ${COLOR_PROFIT}88` : `0 0 16px ${COLOR_GOLD}88`,
          }}
        />
      </div>
    </div>
  );
};

const EnginePulse = ({ lastCycleAt }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const age = secondsSince(lastCycleAt);
  // 0-15s = healthy green, 16-60s = warning gold, 60+ = critical red.
  let color;
  let state;
  if (age == null) {
    color = 'var(--sc-text-mute)';
    state = 'Awaiting first cycle';
  } else if (age <= 15) {
    color = COLOR_PROFIT;
    state = `Engine Live · last cycle ${age}s ago`;
  } else if (age <= 60) {
    color = COLOR_GOLD;
    state = `Engine Idle · last cycle ${age}s ago`;
  } else {
    color = '#EF4444';
    state = `Engine Stalled · last cycle ${age}s ago`;
  }
  // Ensure component re-renders with `now` (already does via setNow); no-op use:
  void now;
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] uppercase tracking-[0.18em]"
      style={{
        color,
        background: 'rgba(0,0,0,0.32)',
        borderColor: `${color}55`,
      }}
      data-testid="admin-engine-pulse"
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: color }} aria-hidden="true" />
      <Activity className="w-3 h-3" aria-hidden="true" />
      {state}
    </div>
  );
};

const DashboardSubpage = ({ admin }) => {
  const [stats, setStats] = useState(null);
  const [profit, setProfit] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  // God-only signals — profit ledger + P&L breakdowns — are gated server-
  // side. Sub-admins (is_god_account=false) MUST NOT call these endpoints
  // or they'll receive 403s. We probe the cheap, role-neutral signals for
  // everyone and conditionally probe the god-only ones for the operator.
  const isGod = Boolean(admin?.is_god_account);

  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        // Always-allowed calls (platform stats + config are admin-tier,
        // not god-tier — every admin can see them).
        const baseCalls = [fetchAdminPlatformStats(), fetchAdminConfig()];
        // God-only call — profit ledger — only fired for the operator.
        const godCalls = isGod ? [fetchAdminProfit('all')] : [];
        const results = await Promise.all([...baseCalls, ...godCalls]);
        if (!alive) return;
        setStats(results[0]);
        setConfig(results[1]);
        setProfit(isGod ? results[2] : null);
        setErrored(false);
      } catch {
        if (!alive) return;
        setErrored(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    probe();
    // Slow-poll (2025-06): admin dashboard widgets refresh once per minute
    // to keep server load low; F5 always pulls instant fresh data.
    const t = setInterval(probe, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [isGod]);

  if (loading && !stats) {
    return (
      <section className="sc-glass-strong rounded-2xl p-6 md:p-8" data-testid="admin-dashboard-loading">
        <div className="flex items-center gap-3 text-[var(--sc-text-mute)] text-[12px] uppercase tracking-[0.22em]">
          <Loader2 className="w-4 h-4 animate-spin text-[#FFC20E]" />
          Calculating platform snapshot…
        </div>
      </section>
    );
  }

  if (errored || !stats) {
    return (
      <section className="sc-glass-strong rounded-2xl p-6 md:p-8" data-testid="admin-dashboard-error">
        <div className="text-[12px] text-[var(--sc-text-mute)]">Platform snapshot temporarily unavailable. Reconnecting…</div>
      </section>
    );
  }

  const adminProfit = Number(profit?.summary?.total ?? profit?.total_usd ?? 0);
  const profitCeiling = parseFloat(config?.admin_alert_ceiling ?? '0');

  return (
    <div className="space-y-6 md:space-y-7" data-testid="admin-dashboard">
      {/* HERO — Platform Net Profit (god-admin only — uses /api/admin/profit) */}
      {isGod ? (
        <section
          className="sc-glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden"
          style={{
            border: '1px solid rgba(255, 182, 18, 0.42)',
            boxShadow: '0 0 0 1px rgba(255,182,18,0.10), 0 30px 80px -20px rgba(255,182,18,0.35)',
          }}
          data-testid="admin-profit-hero"
        >
          <div
            className="absolute -top-24 -right-24 w-72 h-72 pointer-events-none rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,182,18,0.22), transparent 65%)',
            }}
            aria-hidden="true"
          />
          <div
            className="absolute -bottom-24 -left-24 w-72 h-72 pointer-events-none rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,182,18,0.10), transparent 65%)',
            }}
            aria-hidden="true"
          />

          {/* Top strip */}
          <div className="relative z-10 flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #FFC20E, #FFB612)',
                }}
                aria-hidden="true"
              >
                <ShieldCheck className="w-4 h-4 text-[#0a0a0b]" strokeWidth={2.6} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm whitespace-nowrap">
                  1Simple<span className="sc-gold-text">Cycler</span>
                  <span className="text-[var(--sc-text-mute)] text-[11px] ml-1">3.0</span>
                </div>
                <div className="text-[11px] text-[var(--sc-text-dim)] truncate">@{admin?.username || 'operator'} · Platform Snapshot</div>
              </div>
            </div>
            <EnginePulse lastCycleAt={stats.engine_last_cycle_at} />
          </div>

          {/* Hero number */}
          <div className="relative z-10 mt-6 md:mt-7 text-center">
            <div className="text-[10px] uppercase tracking-[0.30em] text-[var(--sc-text-mute)]">Platform Net Profit</div>
            <div
              className="sc-tabular font-display font-extrabold mt-2 leading-none flex items-center justify-center gap-3"
              style={{
                color: COLOR_PROFIT,
                fontSize: 'clamp(40px, 7vw, 64px)',
                textShadow: `0 0 24px ${COLOR_PROFIT}33`,
              }}
              data-testid="admin-profit-hero-value"
            >
              <TrendingUp className="shrink-0" style={{ width: '0.7em', height: '0.7em' }} strokeWidth={2.4} aria-hidden="true" />
              <span>+${fmtUsd(adminProfit)}</span>
            </div>
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] px-3 py-1 rounded-full border"
              style={{
                color: COLOR_PROFIT,
                borderColor: `${COLOR_PROFIT}55`,
                background: `${COLOR_PROFIT}14`,
              }}
            >
              <Trophy className="w-3.5 h-3.5" aria-hidden="true" />
              Lifetime Operator Revenue
            </div>
            <p className="mt-3 text-[12px] text-[var(--sc-text-dim)] max-w-xl mx-auto leading-snug">
              Cumulative platform fees and orphan-match credits collected by the admin pool. Every cycle on the network has contributed.
            </p>
            <ProfitCeilingGauge current={adminProfit} ceiling={profitCeiling} />
          </div>
        </section>
      ) : null}

      {/* Operator P&L — External cash flow + Internal liquidity
          (god-only — hits /api/admin/pnl/external + /api/admin/pnl/internal) */}
      {isGod ? (
        <>
          <ExternalPnLCard />
          <InternalPnLCard />
        </>
      ) : null}

      {/* Platform Stats strip */}
      <section className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="admin-platform-stats">
        <div
          className="absolute -top-16 -right-16 w-44 h-44 pointer-events-none rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.12), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Network Footprint</div>
          <span
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border"
            style={{
              color: '#FFE08A',
              background: 'rgba(255,182,18,0.10)',
              borderColor: 'rgba(255,182,18,0.32)',
            }}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFC20E] animate-pulse" aria-hidden="true" />
            Live
          </span>
        </div>
        <div className="relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Stat icon={Users2} label="Total Members" value={fmtInt(stats.total_members)} testid="admin-stat-members" />
          <Stat icon={Layers3} label="Total Positions" value={fmtInt(stats.total_positions)} testid="admin-stat-positions" />
          <Stat icon={Repeat2} label="Total Cycles" value={fmtInt(stats.total_cycles)} testid="admin-stat-cycles" />
          <Stat icon={Coins} label="Total Volume (USD)" value={`$${fmtUsd(stats.total_volume_usd)}`} testid="admin-stat-volume" />
        </div>
      </section>

      {/* Top 5 Earning Members — inspirational */}
      <section className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="admin-top-earners">
        <div
          className="absolute -top-16 -right-16 w-44 h-44 pointer-events-none rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.14), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] flex items-center gap-2">
            <Crown className="w-3.5 h-3.5 text-[#FFC20E]" />
            Top 5 Earning Members
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">Lifetime · Gross</span>
        </div>
        {stats.top_earners.length === 0 ? (
          <div className="relative z-10 text-[12px] text-[var(--sc-text-mute)] py-6 text-center">
            No member earnings yet. As members cycle, the leaderboard fills.
          </div>
        ) : (
          <ul className="relative z-10 space-y-2" data-testid="admin-top-earners-list">
            {stats.top_earners.map((row, i) => (
              <li
                key={row.user_id || i}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg border"
                style={{
                  background: 'rgba(0,0,0,0.32)',
                  borderColor: 'rgba(255, 182, 18, 0.18)',
                }}
                data-testid={`admin-top-earner-row-${i + 1}`}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 sc-tabular"
                  style={{
                    background: i === 0 ? 'linear-gradient(135deg, #FFC20E, #FFB612)' : 'rgba(255,182,18,0.10)',
                    color: i === 0 ? '#0a0a0b' : '#FFE08A',
                    border: i === 0 ? '1px solid rgba(255,182,18,0.6)' : '1px solid rgba(255,182,18,0.28)',
                  }}
                >
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0 truncate text-[13px]">
                  @<span className="font-semibold">{row.username}</span>
                </div>
                <div className="sc-tabular font-display font-bold text-sm sc-gold-text shrink-0" data-testid={`admin-top-earner-amount-${i + 1}`}>
                  ${fmtUsd(row.earnings)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default DashboardSubpage;
